import { useSelector } from 'react-redux';
import {
  selectSessionStatus,
  selectNTadministrator,
  selectImpersonationMode,
  selectImpersonationStatus,
} from 'features/session/sessionSlice';
import { useGetParameterDictionaryQuery } from 'api/parameter.api';
import { useGetTenantQuery } from 'api/tenant.api';

const SUPPORTED_LANGUAGES_PARAMETER_NAME = 'Supported_Languages';

export function useAvailableLanguages() {
  const sessionStatus = useSelector(selectSessionStatus);
  const isNtAdministrator = useSelector(selectNTadministrator);
  const isImpersonationMode = useSelector(selectImpersonationMode);
  const impersonationStatus = useSelector(selectImpersonationStatus);
  
  // NT Admins are only "ready" once they've started impersonating a tenant,
  // since they don't have their own session in the normal sense.
  const isSessionReady = isNtAdministrator
    ? (isImpersonationMode !== null && impersonationStatus === 'active')
    : sessionStatus === 'active';

  const { data: parameterDictionary } = useGetParameterDictionaryQuery();
  const { data: tenant } = useGetTenantQuery(undefined, {
      skip: !isSessionReady,
    }
  );

  const dictionaryLanguages = parameterDictionary
    ?.find((entry) => entry.name === SUPPORTED_LANGUAGES_PARAMETER_NAME)
    ?.options || [];

  // Parameter dictionary takes priority over tenant config when populated.
  if (dictionaryLanguages.length > 0) {
    return dictionaryLanguages;
  }

  if (Array.isArray(tenant?.supported_languages) && tenant.supported_languages.length > 0) {
    return tenant.supported_languages;
  }

  return [];
}

/**
 * Returns the environment's configured Supported Languages list, or null when
 * none is configured (locale selection is then unrestricted). Centralizes the
 * `Array.isArray(...) && length > 0` guard reused across the location form.
 */
export function getTenantSupportedLanguages(tenant) {
  return Array.isArray(tenant?.supported_languages) &&
    tenant.supported_languages.length > 0
    ? tenant.supported_languages
    : null;
}

/**
 * True when a Supported Languages set is configured and `locale` is not a
 * member of it — i.e. the locale has drifted outside the set. A missing/empty
 * locale counts as outside the set, so a blank locale can never be persisted.
 * When no set is configured nothing is "outside" (returns false).
 */
export function isLocaleOutsideSupportedLanguages(locale, supportedLanguages) {
  if (!Array.isArray(supportedLanguages) || supportedLanguages.length === 0) {
    return false;
  }
  return !locale || !supportedLanguages.includes(locale);
}

/**
 * Resolves the locale to use as the environment default — both the new-location
 * default and the drift-reset target. Prefers the tenant's base_locale, but only
 * when it is selectable under the Supported Languages set; otherwise the first
 * supported language, so the result is always a valid, selectable value.
 */
export function resolveEnvDefaultLocale(tenant, fallback) {
  const supported = getTenantSupportedLanguages(tenant) ?? [];
  const baseLocale = tenant?.base_locale;
  if (baseLocale && (supported.length === 0 || supported.includes(baseLocale))) {
    return baseLocale;
  }
  return supported[0] ?? baseLocale ?? fallback ?? '';
}
